import { useCallback, useEffect, useRef } from 'react';
import { MDText } from 'i18n-react';
import { useRouter } from 'next/router';

import { Hyperlink } from '@motorway/mw-highway-code/alpha';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import cypressIds from 'Components/cypress_ids.json';
import { applyCypressData } from 'Utilities/helpers';

import type { VehicleMutationPayload, VehicleMutationResponse } from './useUpdateDeliveryStatus';
import { useUpdateDeliveryStatus } from './useUpdateDeliveryStatus';
import { triggerUndoDeliveryStatusAnalyticsEvent } from './useUpdateDeliveryStatus.analytics';
import LocalTexts from './useUpdateDeliveryStatus.texts';

const LocalT = new MDText(LocalTexts);

export type UseSuccessToast = {
	triggerSuccessToast: (data: VehicleMutationResponse, variables: VehicleMutationPayload) => void;
};

export const useSuccessToast = (): UseSuccessToast => {
	const closeToast = useRef<() => void>();

	const router = useRouter();

	const dismissToast = useCallback(() => {
		closeToast.current?.();
	}, []);

	useEffect(() => {
		const handleRouteChange = () => {
			dismissToast();
		};

		router.events.on('routeChangeStart', handleRouteChange);

		return () => {
			router.events.off('routeChangeStart', handleRouteChange);
		};
	}, [router, dismissToast]);

	const { mutate: mutateDeliveryStatus } = useUpdateDeliveryStatus();

	const onUndoClickHandler = useCallback(
		(variables: VehicleMutationPayload) => {
			mutateDeliveryStatus(
				{
					deliveredStatus: !variables.deliveredStatus,
					enquiryId: variables.enquiryId,
				},
				{
					onSuccess: () => {
						closeToast.current = toast({
							content: LocalT.translate('undo.action'),
							duration: 5000,
							sequenceKey: `delivered-action-${variables.enquiryId}`,
							variant: 'success',
						});
					},
				},
			);

			triggerUndoDeliveryStatusAnalyticsEvent(variables.enquiryId);
		},
		[mutateDeliveryStatus],
	);

	const triggerSuccessToast = useCallback(
		(_: VehicleMutationResponse, variables: VehicleMutationPayload) => {
			closeToast.current = toast({
				content: (
					<div>
						<span>
							{variables.deliveredStatus ? LocalT.translate('success.marked') : LocalT.translate('success.unmarked')}
						</span>
						<span style={{ marginLeft: '8px' }}>
							<Hyperlink
								{...applyCypressData(cypressIds.buttons.purchasesCompleteUndoDeliveredAction)}
								data-thc-hyperlink
								as="button"
								label={LocalT.translate('undo.label')}
								onClick={() => onUndoClickHandler(variables)}
							>
								Undo
							</Hyperlink>
						</span>
					</div>
				),
				duration: 5000,
				sequenceKey: `delivered-action-${variables.enquiryId}`,
				variant: 'success',
			});
		},
		[onUndoClickHandler],
	);

	return { triggerSuccessToast };
};
